/**
 * The four states every data surface has, written once.
 *
 * Loading, empty, error and forbidden. They live here rather than in each page
 * because they are where an application's quality actually shows: a blank
 * screen, an empty table with no explanation, or "Something went wrong" are all
 * the same bug — the screen knows something the user does not, and is not
 * saying it.
 *
 * Each of these answers three questions: what happened, why, and what to do
 * next. An empty state with no third answer is a dead end.
 */

import type { ReactNode } from 'react';
import {
  AlertTriangle,
  Inbox,
  Layers,
  Loader2,
  Lock,
  RefreshCw,
  SearchX,
  ToggleLeft,
} from 'lucide-react';

import { cn } from '../lib/cn';
import { Button } from './ui/button';
import { queryView, type QueryLike } from '../lib/query-state';

/** A centred, bounded frame so every state sits in the same place on screen. */
function Frame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex min-h-60 flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Icon({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'danger' | 'warning' }) {
  return (
    <div
      className={cn(
        'flex size-11 items-center justify-center rounded-full',
        tone === 'muted' && 'bg-surface-2 text-faint',
        tone === 'danger' && 'bg-danger-subtle text-danger',
        tone === 'warning' && 'bg-warning-subtle text-warning',
      )}
      aria-hidden
    >
      {children}
    </div>
  );
}

/**
 * Loading.
 *
 * Takes a label, because a bare spinner says only "wait" — and after two
 * seconds the user cannot tell waiting from broken. Where the shape of the
 * result is known, prefer {@link TableSkeleton}: a skeleton tells the reader
 * what is arriving.
 */
export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <Frame>
      <Loader2 className="size-6 animate-spin text-accent" aria-hidden />
      {/* Announced politely so a screen reader says it once, without
          interrupting whatever the user was reading. */}
      <p className="text-sm text-muted" role="status" aria-live="polite">
        {label}…
      </p>
    </Frame>
  );
}

/** A table-shaped placeholder. Preferred over a spinner where a table is coming. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="w-full animate-fade-in overflow-hidden" aria-hidden>
      <div className="flex gap-3 border-b border-line px-4 py-3">
        {Array.from({ length: columns }).map((_, index) => (
          <div key={index} className="h-3 flex-1 rounded bg-surface-2" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_row, rowIndex) => (
        <div key={rowIndex} className="flex gap-3 border-b border-line/60 px-4 py-3.5">
          {Array.from({ length: columns }).map((_cell, columnIndex) => (
            <div
              key={columnIndex}
              className="h-3 flex-1 rounded bg-surface-2"
              // Staggered widths so it reads as content rather than a grid of
              // identical bars, which looks like a rendering fault.
              style={{ opacity: 1 - rowIndex * 0.06, width: columnIndex === 0 ? '18%' : undefined }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Empty — the register genuinely holds nothing.
 *
 * Distinct from {@link NoMatches} on purpose. "You have no clients yet" invites
 * an action; "no clients match these filters" invites clearing a filter. One
 * message for both is wrong half the time.
 */
export function EmptyState({
  title = 'Nothing here yet',
  description,
  action,
  icon,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Frame>
      <Icon>{icon ?? <Inbox className="size-5" />}</Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">{title}</p>
        {description ? <p className="max-w-sm text-sm text-muted">{description}</p> : null}
      </div>
      {action}
    </Frame>
  );
}

/** Nothing matched the current filters — with the way out. */
export function NoMatches({ onClear }: { onClear?: () => void }) {
  return (
    <Frame>
      <Icon>
        <SearchX className="size-5" />
      </Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">No records match these filters</p>
        <p className="max-w-sm text-sm text-muted">
          There are records here, but none of them match what you have narrowed to.
        </p>
      </div>
      {onClear ? (
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      ) : null}
    </Frame>
  );
}

/**
 * An error, with what happened and what to do.
 *
 * The request id is shown when there is one. That is the whole point of putting
 * it in the envelope: it lets somebody report "this failed" and have it looked
 * up, without the response disclosing anything about why.
 */
export function ErrorState({
  title = 'That did not work',
  message,
  requestId,
  onRetry,
}: {
  title?: string;
  message?: string;
  requestId?: string;
  onRetry?: () => void;
}) {
  return (
    <Frame>
      <Icon tone="danger">
        <AlertTriangle className="size-5" />
      </Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">{title}</p>
        <p className="max-w-md text-sm text-muted">
          {message ?? 'The server could not complete that request.'}
        </p>
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RefreshCw className="size-3.5" />
          Try again
        </Button>
      ) : null}
      {requestId ? (
        <p className="text-2xs text-faint">
          Reference <span className="font-mono">{requestId}</span>
        </p>
      ) : null}
    </Frame>
  );
}

/** The user's access does not cover this page. */
export function Forbidden({
  message = 'Your access does not cover this page. An administrator can change what your role includes.',
}: {
  message?: string;
}) {
  return (
    <Frame>
      <Icon tone="warning">
        <Lock className="size-5" />
      </Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">You cannot open this</p>
        <p className="max-w-md text-sm text-muted">{message}</p>
      </div>
    </Frame>
  );
}

/**
 * The page belongs to a service other than the one being worked in.
 *
 * Not the forbidden state and not the disabled one: the client holds this
 * service, and the only thing in the way is which one the portal is narrowed
 * to. So the remedy is on the page — one button — rather than an instruction to
 * go and find the switcher.
 */
export function NotTheActiveService({
  activeLabel,
  options,
  onChoose,
}: {
  activeLabel: string;
  /** The client's entries that own this page, usually one. */
  options: readonly { id: string; label: string }[];
  onChoose: (id: string) => void;
}) {
  return (
    <Frame>
      <Icon tone="muted">
        <Layers className="size-5" />
      </Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">That belongs to another service</p>
        <p className="max-w-md text-sm text-muted">
          You are working in {activeLabel}. Switch to open this page; the tabs change with it.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {options.map((option) => (
          <Button key={option.id} size="sm" onClick={() => onChoose(option.id)}>
            Switch to {option.label}
          </Button>
        ))}
      </div>
    </Frame>
  );
}

/**
 * The installation does not have this service.
 *
 * Deliberately not the forbidden state. The remedy is a switch on the Services
 * screen, not a change to anybody's role, and saying so is the difference
 * between a one-click fix and a support conversation about permissions.
 */
export function ServiceDisabled({ service }: { service?: string }) {
  return (
    <Frame>
      <Icon tone="warning">
        <ToggleLeft className="size-5" />
      </Icon>
      <div className="space-y-1">
        <p className="text-sm font-medium text-heading">This service is not enabled</p>
        <p className="max-w-md text-sm text-muted">
          {service ? `The ${service} service` : 'This service'} is switched off for this
          installation. An administrator can turn it on under Settings → Services.
        </p>
      </div>
    </Frame>
  );
}

/**
 * What stands in for a request's data until it is here: {@link LoadingState}
 * while it comes, {@link ErrorState} with a retry when it failed. For the
 * early return — `if (q.data === undefined) return <QueryPending query={q} />`
 * — where a bare "loading or no data" check spun for ever on a failure.
 */
export function QueryPending({
  query,
  label,
  loading,
  errorMessage,
}: {
  query: QueryLike<unknown>;
  label?: string;
  /** What to draw while it comes, where the result's shape is known (a
   * skeleton); the labelled {@link LoadingState} otherwise. */
  loading?: ReactNode;
  errorMessage?: string;
}) {
  const view = queryView(query, errorMessage);
  if (view.kind === 'error')
    return <ErrorState message={view.message} requestId={view.requestId} onRetry={view.retry} />;
  return loading ?? <LoadingState label={label} />;
}

/** {@link QueryPending} until the data is here, then `children(data)`. */
export function QueryState<T>({
  query,
  label,
  loading,
  errorMessage,
  children,
}: {
  query: QueryLike<T>;
  label?: string;
  loading?: ReactNode;
  errorMessage?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  return <QueryPending query={query} label={label} loading={loading} errorMessage={errorMessage} />;
}
